import { FC } from 'react';
import { Skeleton, Spacer, Stack } from '@chakra-ui/react';
import { Helmet } from 'react-helmet-async';
import { generatePath, useParams, useSearchParams } from 'react-router-dom';

import { Breadcrumbs } from '../../components/breadcrumbs/breadcrumbs';
import { FileDetails } from '../../components/file-details/file-details';
import { DEFAULT_ERROR_SIZE } from '../../components/file-processes/file-process-errors';
import { FileVersionHistory } from '../../components/file-version-history/file-version-history';
import {
  useVersionHistoryDrawer,
  VersionHistoryDrawer,
} from '../../components/file-version-history/version-history-drawer';
import * as Routes from '../../constants/routes';
import { useGetFileQuery } from '../../graphql/generated';
import { useEnableDynamicFilePolling } from '../../hooks/use-enable-file-polling/use-enable-file-polling';
import { DebounceLoadingState } from '../../ui/debounce-loading/debounce-loading';
import { FileDetailsLayout } from './file-details-layout';
import { FileErrorState } from './file-error-state';

export type FileDetailsPageParams = {
  cedantId: string;
  fileId: string;
};

export const navigateToFileDetailsRoute = (
  params: FileDetailsPageParams
): string =>
  generatePath(Routes.FILE_DETAILS, {
    cedantId: params.cedantId,
    fileId: params.fileId,
  });

const LoadingState = () => (
  <Stack>
    <Skeleton height="12" width="60" />
    <Spacer minHeight="6" />
    <Skeleton height="10" width="60" />
    <Spacer minHeight="6" />
    <Skeleton height="5" width="80" />
    <Skeleton height="5" width="80" />
    <Spacer minHeight="2" />
    <Skeleton height="5" width="80" />
    <Skeleton height="5" width="80" />
    <Spacer minHeight="2" />
    <Skeleton height="5" width="80" />
    <Skeleton height="5" width="80" />
    <Spacer minHeight="2" />
    <Skeleton height="12" width="40" />
  </Stack>
);

export const FileDetailsPage: FC = () => {
  const { cedantId, fileId } = useParams() as FileDetailsPageParams;

  const [searchParams] = useSearchParams();
  const versionHistory = searchParams.get('versionHistory');

  const { closeDrawer, openDrawer, isOpen } = useVersionHistoryDrawer(
    Boolean(versionHistory)
  );
  const { data, error, refetch, startPolling, stopPolling } = useGetFileQuery({
    variables: {
      cedant: cedantId,
      fileId,
      errorsPagination: {
        limit: DEFAULT_ERROR_SIZE,
        offset: 0, // Not expected to change
      },
    },
    fetchPolicy: 'cache-and-network',
  });
  useEnableDynamicFilePolling({
    startPolling,
    stopPolling,
    data: data?.cedant?.file.status,
  });

  if (error) {
    return <FileErrorState error={error} />;
  }

  if (!data) {
    return (
      <FileDetailsLayout>
        <DebounceLoadingState>
          <LoadingState />
        </DebounceLoadingState>
      </FileDetailsLayout>
    );
  }

  if (!data.cedant?.file) {
    return <FileErrorState />;
  }

  return (
    <>
      <Helmet>
        <title>{data.cedant.file.name}</title>
      </Helmet>
      <FileDetailsLayout>
        <Breadcrumbs
          cedantId={data.cedant.file.cedant}
          breadcrumbs={data.cedant.file.breadcrumbs}
        />
        <FileDetails file={data.cedant.file} refetchFileDetails={refetch} />
        {data.cedant.file.versions.length > 1 && (
          <VersionHistoryDrawer
            isOpen={isOpen}
            openDrawer={openDrawer}
            closeDrawer={closeDrawer}
          >
            <FileVersionHistory
              cedantId={cedantId}
              fileId={data.cedant.file.id}
            />
          </VersionHistoryDrawer>
        )}
      </FileDetailsLayout>
    </>
  );
};
